import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import "../../Styles/Seller/SellerAddEditModal.css";
import { showError, showSuccess } from "../../utils/Toast";
import {
  DEFAULT_PRODUCT_IMAGE,
  withDefaultProductImage,
} from "../../utils/productPresentation";

import { useDropzone } from "react-dropzone";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axiosInstance from "../../utils/AxiosConfig";

const MAX_IMAGES = 6;

function makeFileItem(file) {
  return {
    id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    file,
    preview: URL.createObjectURL(file),
    isNew: true,
    url: null,
  };
}

function makeUrlItem(url) {
  return {
    id: `url_${Math.random().toString(36).slice(2, 8)}`,
    file: null,
    preview: url,
    isNew: false,
    url,
  };
}

/* Sortable thumbnail component using dnd-kit useSortable */
function SortableThumb({ item, index, onRemove, onReplace }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const stopPointer = (e) => {
    e.stopPropagation();
  };

  const handleReplaceClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onReplace) onReplace(item.id);
  };

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove) onRemove(item.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="thumb"
      {...attributes}
      {...listeners}
      tabIndex={0}
      aria-label="Product image thumbnail"
    >
      <img src={item.preview} alt="thumb" className="thumb-img" />
      {/* Main image badge */}
      {index === 0 && (
        <div className="main-image-badge">
          <span>Main Image</span>
        </div>
      )}
      <div className="thumb-actions">
        <button
          type="button"
          className="sellerdash-btn sellerdash-btn-outline"
          onPointerDown={stopPointer}
          onMouseDown={stopPointer}
          onClick={handleReplaceClick}
        >
          Replace
        </button>
        <button
          type="button"
          className="sellerdash-btn sellerdash-btn-outline thumb-remove-btn"
          onPointerDown={stopPointer}
          onMouseDown={stopPointer}
          onClick={handleRemoveClick}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function SelectField({
  label,
  required = false,
  value,
  onChange,
  onBlur,
  invalid = false,
  errorMessage = "",
  options = [],
  placeholder,
  disabled = false,
}) {
  return (
    <label className={invalid ? "field-error" : ""}>
      <div>
        <span>{label}</span> {required && <span className="required-star">*</span>}
      </div>
      <select
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      {invalid && <span className="error-message">{errorMessage}</span>}
    </label>
  );
}

function AddEditProductModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  insertProductRequest,
  getProductRequest,
  categorydata,
  compainesdata,
  editProductRequest,
  subcategorydata,
  branddata,
  mode = "product",
}) {
  // console.log("initialData", initialData);
  const [form, setForm] = useState({
    id: null,
    title: "",
    categoryid: "",
    companyid: "",
    brandid: "",
    description: "",
    price: "",
    oldPrice: "",
    discount: "",
    tagText: "",
    badge: "",
    rating: "",
    inventory: 0,
    featured: false,
    active: false,
    status: "inactive",
    features: [],
    attributes: [],
    subcategoryid: "",
  });

  // Track which fields have been touched for validation
  const [touchedFields, setTouchedFields] = useState({
    title: false,
    categoryid: false,
    subcategoryid: false,
    brandid: false,
    price: false,
    inventory: false,
  });

  // attributes inputs (key & value) like features UI
  const [attrKeyInput, setAttrKeyInput] = useState("");
  const [attrValueInput, setAttrValueInput] = useState("");

  const [items, setItems] = useState([]); // images array
  const [loading, setLoading] = useState(false);
  const thumbsRef = useRef(null);
  // const [subCategory, setSubCategory] = useState([]);

  // useDropzone
  const onDrop = (acceptedFiles) => {
    if (!acceptedFiles || !acceptedFiles.length) return;
    setItems((prev) => {
      const room = MAX_IMAGES - prev.length;
      const toAdd = acceptedFiles.slice(0, room).map((f) => makeFileItem(f));
      return [...prev, ...toAdd];
    });
  };
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    maxFiles: MAX_IMAGES,
    noClick: true,
  });

  useEffect(() => {
    if (initialData) {
      let attrArray = [];
      if (initialData.attributes) {
        try {
          const parsed =
            typeof initialData.attributes === "string"
              ? JSON.parse(initialData.attributes)
              : initialData.attributes;
          if (parsed && typeof parsed === "object") {
            attrArray = Object.keys(parsed).map((k) => ({
              key: k,
              value: parsed[k],
            }));
          } else {
            attrArray = [{ key: "raw", value: String(initialData.attributes) }];
          }
        } catch (e) {
          attrArray = [{ key: "raw", value: String(initialData.attributes) }];
        }
      }

      let loadedFeatures = [];
      const featureSource =
        initialData.features !== undefined ? initialData.features : initialData.featured;
      if (Array.isArray(featureSource)) {
        loadedFeatures = featureSource.map((feature) => String(feature));
      } else if (
        typeof featureSource === "string" ||
        typeof featureSource === "number" ||
        typeof featureSource === "boolean"
      ) {
        const featureValue = String(featureSource).trim();
        if (featureValue.startsWith("[") && featureValue.endsWith("]")) {
          try {
            const parsedFeatures = JSON.parse(featureValue);
            if (Array.isArray(parsedFeatures)) {
              loadedFeatures = parsedFeatures.map((feature) => String(feature));
            }
          } catch {
            loadedFeatures = featureValue ? [featureValue] : [];
          }
        } else if (featureValue) {
          loadedFeatures = [featureValue];
        }
      }

      setForm({
        id: initialData.id ?? null,
        title: initialData.name ?? initialData.title ?? "",
        categoryid: initialData.categoryid ?? initialData.category_id ?? "",
        companyid: initialData.companyid ?? initialData.company_id ?? "",
        brandid: initialData.brandid ?? initialData.brand_id ?? "",
        description: initialData.description ?? initialData.tagText ?? "",
        price: initialData.selling_price ?? initialData.price ?? "",
        oldPrice: initialData.oldPrice ?? "",
        discount: initialData.discount ?? "",
        tagText: initialData.tagText ?? initialData.subtitle ?? "",
        badge: initialData.badge ?? "",
        rating: initialData.rating ?? 0,
        inventory: initialData.inventory ?? initialData.stock ?? 0,
        featured: Boolean(initialData.featured ?? 0),
        active: initialData.active ?? initialData.status === "active",
        status: initialData.status ?? "active",
        features: loadedFeatures,
        attributes: attrArray,
        subcategoryid:
          initialData.subcategoryid ?? initialData.sub_category_id ?? "",
      });

      const prefills = [];
      if (Array.isArray(initialData.images) && initialData.images.length) {
        initialData.images
          .slice(0, MAX_IMAGES)
          .forEach((u) => prefills.push(makeUrlItem(u)));
      } else if (initialData.imageUrl || initialData.img) {
        prefills.push(makeUrlItem(initialData.imageUrl || initialData.img));
      }
      setItems(prefills);
    } else {
      setForm((f) => ({
        ...f,
        id: null,
        name: "",
        title: "",
        categoryid: "",
        companyid: "",
        brandid: "",
        description: "",
        price: "",
        oldPrice: "",
        discount: "",
        tagText: "",
        badge: "",
        rating: "",
        inventory: 0,
        featured: false,
        active: false,
        status: "inactive",
        features: [],
        attributes: [],
        subcategoryid: "",
      }));
      setItems([]);
      setAttrKeyInput("");
      setAttrValueInput("");
    }

    // Reset touched fields when modal opens/closes
    setTouchedFields({
      title: false,
      categoryid: false,
      subcategoryid: false,
      brandid: false,
      price: false,
      inventory: false,
    });

    return () => {
      items.forEach((it) => {
        if (it.isNew && it.preview) URL.revokeObjectURL(it.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen]);

  function update(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  // Handle field blur to mark as touched
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Feature checkbox: toggles presence of "1" in form.features
  function toggleFeaturedCheckbox() {
    setForm((s) => {
      const hasOne =
        Array.isArray(s.features) && s.features.some((f) => String(f) === "1");
      if (hasOne) {
        return { ...s, features: s.features.filter((f) => String(f) !== "1") };
      } else {
        return {
          ...s,
          features: [...(Array.isArray(s.features) ? s.features : []), "1"],
        };
      }
    });
  }

  // Attribute helpers
  function addAttribute() {
    const key = String(attrKeyInput || "").trim();
    const valRaw = String(attrValueInput || "").trim();
    if (!key) return;
    const exists = form.attributes.some(
      (a) => a.key.toLowerCase() === key.toLowerCase(),
    );
    if (exists) {
      setForm((s) => ({
        ...s,
        attributes: s.attributes.map((a) =>
          a.key.toLowerCase() === key.toLowerCase()
            ? { key, value: coerceValue(valRaw) }
            : a,
        ),
      }));
      setAttrKeyInput("");
      setAttrValueInput("");
      return;
    }
    setForm((s) => ({
      ...s,
      attributes: [...s.attributes, { key, value: coerceValue(valRaw) }],
    }));
    setAttrKeyInput("");
    setAttrValueInput("");
  }

  function removeAttribute(idx) {
    setForm((s) => ({
      ...s,
      attributes: s.attributes.filter((_, i) => i !== idx),
    }));
  }

  function handleAttrKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!attrValueInput) {
        const el = document.getElementById("attr-value-input");
        if (el) el.focus();
      } else {
        addAttribute();
      }
    }
  }
  function handleAttrValueKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addAttribute();
    }
    if (
      e.key === "Backspace" &&
      attrValueInput === "" &&
      !attrKeyInput &&
      form.attributes.length
    ) {
      removeAttribute(form.attributes.length - 1);
    }
  }

  // DnD and image helpers
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleRemove = (id) => {
    setItems((prev) => {
      const rem = prev.find((p) => p.id === id);
      if (rem && rem.isNew && rem.preview) {
        try {
          URL.revokeObjectURL(rem.preview);
        } catch (err) {
          console.warn("Error revoking URL:", err);
        }
      }
      const newItems = prev.filter((p) => p.id !== id);
      return newItems;
    });
  };

  const handleReplace = (id) => {
    const tmp = document.createElement("input");
    tmp.type = "file";
    tmp.accept = "image/*";
    tmp.multiple = false; // Only single file replacement
    tmp.onchange = (e) => {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      setItems((prev) =>
        prev.map((it) => {
          if (it.id !== id) return it;
          // Revoke old URL if it exists
          if (it.isNew && it.preview) {
            try {
              URL.revokeObjectURL(it.preview);
            } catch (err) {
              console.warn("Error revoking old URL:", err);
            }
          }
          const newItem = makeFileItem(f);
          return newItem;
        }),
      );
    };
    tmp.click();
  };

  useEffect(() => {
    const el = thumbsRef.current;
    if (!el) return;
    function onWheel(e) {
      if (e.shiftKey) return;
      if (el.scrollWidth <= el.clientWidth) return;
      const multiplier = 1.0;
      const delta = e.deltaY * multiplier;
      if (Math.abs(e.deltaX) > 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel, { passive: false });
  }, [items]);

  function coerceValue(v) {
    if (v === "") return "";
    const low = String(v).toLowerCase();
    if (low === "true") return true;
    if (low === "false") return false;
    if (!isNaN(v) && v !== "") return Number(v);
    if (String(v).includes(","))
      return String(v)
        .split(",")
        .map((s) => s.trim());
    return v;
  }

  // Validation function for mandatory fields
  const validateMandatoryFields = () => {
    const errors = [];

    if (!form.title || form.title.trim() === "") {
      errors.push("Title is required");
    }

    if (!form.categoryid || form.categoryid === "") {
      errors.push("Category is required");
    }

    if (!form.subcategoryid || form.subcategoryid === "") {
      errors.push("Subcategory is required");
    }

    if (!form.brandid || form.brandid === "") {
      errors.push("Brand is required");
    }

    if (!form.price || form.price === "" || Number(form.price) <= 0) {
      errors.push("Price is required and must be greater than 0");
    }

    if (form.inventory === "" || form.inventory === null || form.inventory === undefined || Number(form.inventory) < 0) {
      errors.push("Stock quantity is required and must be 0 or greater");
    }

    if (items.length > MAX_IMAGES) {
      errors.push(`You can upload up to ${MAX_IMAGES} product images`);
    }

    return errors;
  };

  /* Network helpers */
  async function postToServer(fd, isUpdate = false) {
    const url = isUpdate ? "updateProduct" : "createProduct";
    const res = await axiosInstance.post(url, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return { res };
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Mark all fields as touched to show validation errors
    setTouchedFields({
      title: true,
      categoryid: true,
      subcategoryid: true,
      brandid: true,
      price: true,
      inventory: true,
    });

    // Validate mandatory fields
    const validationErrors = validateMandatoryFields();

    if (validationErrors.length > 0) {
      // Show first error
      showError(validationErrors[0]);
      return;
    }

    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("name", form.title);
      fd.append("categoryid", form.categoryid || "");
      fd.append("companyid", form.companyid || "");
      fd.append("brand", form.brandid || "");
      fd.append("description", form.description || form.tagText || "");
      fd.append("price", "");
      fd.append("selling_price", String(form.price));
      fd.append("discount", String(form.discount || 0));
      fd.append("stockquantity", String(form.inventory || 0));
      fd.append("featured", form.featured ? "1" : "0");
      fd.append("status", form.status || (form.active ? "active" : "inactive"));
      fd.append("subCategoryid", form.subcategoryid || "");

      if (Array.isArray(form.features) && form.features.length) {
        fd.append("features", JSON.stringify(form.features));
      }

      const attributesObj = {};
      if (Array.isArray(form.attributes)) {
        for (const a of form.attributes) {
          if (!a || !a.key) continue;
          attributesObj[a.key] = a.value;
        }
      }
      fd.append("attributes", JSON.stringify(attributesObj));

      const newFiles = items.filter((i) => i.isNew).map((i) => i.file);
      newFiles.forEach((f) => {
        fd.append("images", f);
      });

      const existingUrls = items.filter((i) => !i.isNew).map((i) => i.url);
      fd.append("existing_images", JSON.stringify(existingUrls));

      if (form.id) {
        fd.append("id", String(form.id));
      }

      const result = await postToServer(fd, Boolean(form.id));

      if (!result.res.data.success) {
        const msg = result.res.data?.message || `Server error`;
        showError(msg);
        setLoading(false);
        return;
      }

      showSuccess(result.res.data.message);
      if (getProductRequest) getProductRequest({ scope: "mine" });
      if (onSubmit) {
        try {
          onSubmit(result.res.data);
        } catch (cbErr) {
          console.error("onSubmit callback threw:", cbErr);
        }
      }

      items.forEach((it) => {
        if (it.isNew && it.preview) {
          try {
            URL.revokeObjectURL(it.preview);
          } catch (revErr) {
            console.warn("revokeObjectURL failed:", revErr);
          }
        }
      });

      setLoading(false);
      onClose();
    } catch (err) {
      console.error("Upload error:", err);
      showError("Upload failed. Check console for details.");
      setLoading(false);
    }
  }

  const isFeaturedChecked =
    Array.isArray(form.features) &&
    form.features.some((f) => String(f) === "1");

  // Helper function to get main image URL for preview
  const getMainImageUrl = () => {
    if (items.length === 0) return null;
    return items[0].preview || items[0].url;
  };

  // Check if a field is invalid (touched and empty)
  const isFieldInvalid = (fieldName) => {
    if (!touchedFields[fieldName]) return false;

    switch (fieldName) {
      case 'title':
        return !form.title || form.title.trim() === '';
      case 'categoryid':
        return !form.categoryid || form.categoryid === '';
      case 'subcategoryid':
        return !form.subcategoryid || form.subcategoryid === '';
      case 'brandid':
        return !form.brandid || form.brandid === '';
      case 'price':
        return !form.price || form.price === '' || Number(form.price) <= 0;
      case 'inventory':
        return form.inventory === '' || form.inventory === null || form.inventory === undefined || Number(form.inventory) < 0;
      default:
        return false;
    }
  };

  // console.log(isFeaturedChecked, form.features);
  const subcategorydatafilter = subcategorydata?.filter(
    (item) => Number(item.category_id) === Number(form.categoryid)
  ) || [];

  // console.log('subcategorydatafilter', subcategorydata && subcategorydata.filter((item) => Number(item.category_id) === Number(form.categoryid)))

  const isRequestMode = mode === "request";
  const modalTitle = form.id
    ? isRequestMode
      ? "Edit Request"
      : "Edit Product"
    : isRequestMode
      ? "Product Request"
      : "Add Product";
  const requestStatusLabel = form.id
    ? "Pending admin approval after submit"
    : "Pending admin approval";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="sellerdash-modal-wrapper">
        <h2>{modalTitle}</h2>

        <form onSubmit={handleSubmit} className="sellerdash-form">
          <div className="sellerdash-form-row">
            <label className={isFieldInvalid('title') ? 'field-error' : ''}>
              <div>
                <span>Title</span> <span className="required-star">*</span>
              </div>
              <input
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                onBlur={() => handleFieldBlur('title')}
                required
              />
              {isFieldInvalid('title') && <span className="error-message">Title is required</span>}
            </label>

            <SelectField
              label="Category"
              required
              value={form.categoryid}
              onChange={(e) => {
                update("categoryid", e.target.value);
                update("subcategoryid", "");
              }}
              onBlur={() => handleFieldBlur('categoryid')}
              invalid={isFieldInvalid('categoryid')}
              errorMessage="Category is required"
              options={Array.isArray(categorydata) ? categorydata : []}
              placeholder="Select category"
            />

            <SelectField
              label="Sub Category"
              required
              value={form.subcategoryid}
              onChange={(e) => update("subcategoryid", e.target.value)}
              onBlur={() => handleFieldBlur('subcategoryid')}
              invalid={isFieldInvalid('subcategoryid')}
              errorMessage="Subcategory is required"
              options={subcategorydatafilter}
              placeholder="Select sub category"
              disabled={!form.categoryid}
            />

            {Array.isArray(compainesdata) && compainesdata.length > 0 && (
              <label>
                <div>
                  <span>Company</span>
                </div>
                <select
                  value={form.companyid}
                  onChange={(e) => update("companyid", e.target.value)}
                >
                  <option value="">Select company</option>
                  {compainesdata.map((data) => (
                    <option key={data.id} value={data.id}>
                      {data.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <SelectField
              label="Brand"
              required
              value={form.brandid}
              onChange={(e) => update("brandid", e.target.value)}
              onBlur={() => handleFieldBlur('brandid')}
              invalid={isFieldInvalid('brandid')}
              errorMessage="Brand is required"
              options={Array.isArray(branddata) ? branddata : []}
              placeholder="Select brand"
            />

            <label className={isFieldInvalid('description') ? 'field-error' : ''}>
              <div>
                <span>Description</span>
              </div>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={3}
              />
            </label>
          </div>

          {/* Images */}
          <label>
            <div>
              <span>Product Images</span>
            </div>
            {/* Product Images (up to {MAX_IMAGES}) */}
          </label>
          <div className="sellerdash-note">
            💡 <strong>Important:</strong> The first image is your main product
            image. It will appear in product listings, thumbnails, and as the
            default view. Drag images to reorder them. Images are optional.
          </div>

          <div
            {...getRootProps({
              className: `multi-image-dropzone ${isDragActive ? "sd-drop-active" : ""}`,
            })}
            style={{ position: "relative" }}
          >
            <input {...getInputProps()} />
            <div
              className="dropzone-instructions"
              onClick={() => openFileDialog()}
            >
              <strong>Drag & drop images here</strong> or click to select files.
              <div className="sellerdash-dropzone-meta">
                Allowed: JPG/PNG. Max {MAX_IMAGES} images. You can save without uploading any image.
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="multi-image-thumbs" ref={thumbsRef}>
                  {items.map((it, index) => (
                    <SortableThumb
                      key={it.id}
                      item={it}
                      index={index}
                      onRemove={handleRemove}
                      onReplace={handleReplace}
                    />
                  ))}

                  {Array.from({ length: MAX_IMAGES - items.length }).map(
                    (_, i) => (
                      <div
                        key={`empty_${i}`}
                        className="thumb empty-thumb"
                        onClick={() => openFileDialog()}
                        role="button"
                        aria-label="Add image"
                      >
                        <div className="thumb-empty-text">+</div>
                      </div>
                    ),
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Main Image Preview */}
          <div className="sellerdash-main-image-preview">
            <div className="sellerdash-main-image-preview__inner">
              <div className="sellerdash-main-image-preview__media">
                <div className="sellerdash-main-image-preview__eyebrow">
                  MAIN IMAGE PREVIEW
                </div>
                <img
                  src={withDefaultProductImage(getMainImageUrl())}
                  alt="Main product"
                  className="sellerdash-main-image-preview__image"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                  }}
                />
              </div>
              <div className="sellerdash-main-image-preview__copy">
                {items.length > 0
                  ? "This image will appear first in all product listings and as the thumbnail."
                  : "No image uploaded. The default product image will be shown until you upload one."}
                <div className="sellerdash-main-image-preview__subcopy">
                  {items.length > 0
                    ? "Drag a different image to the first position if you want to change it."
                    : "You can still submit this request without uploading any image."}
                </div>
              </div>
            </div>
          </div>

          {/* Featured checkbox (outer label has className="featured-row" for single-row CSS) */}
          <label className="featured-row">
            Featured
            <div className="sellerdash-featured-controls">
              <label className="sellerdash-featured-option">
                <input
                  type="checkbox"
                  checked={isFeaturedChecked}
                  onChange={toggleFeaturedCheckbox}
                />
                <span className="featured-comment">
                  Mark as Featured (1 = featured, 0 = not featured)
                </span>
              </label>
            </div>
          </label>

          {/* Attributes */}
          <label>
            Attributes (add multiple key & value pairs)
            <div className="sellerdash-attribute-builder">
              <input
                className="sellerdash-attribute-input sellerdash-attribute-input-key"
                placeholder="Key (e.g. color)"
                value={attrKeyInput}
                onChange={(e) => setAttrKeyInput(e.target.value)}
                onKeyDown={handleAttrKeyDown}
                aria-label="Attribute key"
              />
              <input
                id="attr-value-input"
                className="sellerdash-attribute-input sellerdash-attribute-input-value"
                placeholder="Value (e.g. red or S,M,L)"
                value={attrValueInput}
                onChange={(e) => setAttrValueInput(e.target.value)}
                onKeyDown={handleAttrValueKeyDown}
                aria-label="Attribute value"
              />
              <button
                type="button"
                className="sellerdash-btn sellerdash-btn-outline sellerdash-attribute-add"
                onClick={() => addAttribute()}
              >
                Add
              </button>
            </div>
            <div className="sellerdash-attribute-list">
              {form.attributes && form.attributes.length === 0 && (
                <div className="sellerdash-attribute-empty">
                  No attributes added
                </div>
              )}
              {form.attributes &&
                form.attributes.map((a, idx) => (
                  <div key={idx} className="sellerdash-attribute-chip">
                    <strong className="sellerdash-attribute-chip-key">
                      {a.key}:
                    </strong>
                    <span className="sellerdash-attribute-chip-value">
                      {typeof a.value === "object"
                        ? JSON.stringify(a.value)
                        : String(a.value)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttribute(idx)}
                      className="sellerdash-btn sellerdash-btn-outline sellerdash-attribute-chip-remove"
                      aria-label={`Remove attribute ${a.key}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
            <div className="sellerdash-attribute-hint">
              Tip: for lists enter comma-separated values in the Value field
              (e.g. <code> S,M,L </code>) and it will be parsed into an array.
            </div>
          </label>

          <div className="sellerdash-form-row">
            <label className={isFieldInvalid('price') ? 'field-error' : ''}>
              <div>
                <span>Price</span> <span className="required-star">*</span>
              </div>
              <input
                type="number"
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                onBlur={() => handleFieldBlur('price')}
                required
              />
              {isFieldInvalid('price') && <span className="error-message">Price is required and must be greater than 0</span>}
            </label>
            <label>
              <div>
                <span>Discount</span>
              </div>
              <input
                type="number"
                value={form.discount}
                onChange={(e) => update("discount", e.target.value)}
              />
            </label>
          </div>

          <div className="sellerdash-form-row">
            <label className={isFieldInvalid('inventory') ? 'field-error' : ''}>
              <div>
                <span>Stock Quantity</span> <span className="required-star">*</span>
              </div>
              <input
                type="number"
                value={form.inventory}
                onChange={(e) => update("inventory", e.target.value)}
                onBlur={() => handleFieldBlur('inventory')}
                required
              />
              {isFieldInvalid('inventory') && <span className="error-message">Stock quantity is required and must be 0 or greater</span>}
            </label>
          </div>

          <div className="sellerdash-form-row">
            <label>
              <div>
                <span>Request Status</span>
              </div>
              <input
                value={requestStatusLabel}
                readOnly
                disabled
              />
            </label>
          </div>

          <div className="sellerdash-form-actions">
            <button
              type="button"
              className="sellerdash-btn sellerdash-btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dashboard-export-btn"
              disabled={loading}
            >
              {loading
                ? form.id
                  ? "Saving..."
                  : "Submitting..."
                : form.id
                  ? isRequestMode
                    ? "Update Request"
                    : "Save changes"
                  : isRequestMode
                    ? "Submit Request"
                    : "Create Product"}
            </button>
          </div>
          {isRequestMode && (
            <div className="sellerdash-note">
              <strong>Note:</strong> Product requests are submitted as pending and stay hidden from customers until an admin approves them.
            </div>
          )}

        </form>
      </div>
    </Modal>
  );
}

AddEditProductModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  initialData: PropTypes.object,
  mode: PropTypes.string,
};

AddEditProductModal.defaultProps = {
  isOpen: false,
  initialData: null,
  onSubmit: () => { },
  mode: "product",
};

export default AddEditProductModal; 
